import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { LoadingSpinner, ModalOpenButton, useScreenWidth } from "@msys/ui";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Navigate, Route, Routes, useMatch, useParams } from "react-router-dom";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import { LayoutView } from "../../../commons/button/ToggleLayoutViewButtonGroup.js";
import { PageNotFound } from "../../../commons/PageNotFound.js";
import { usePageWidth } from "../../../commons/hooks/usePageWidth.js";
import { PageTopbarItem } from "../../../commons/layout/Page.js";
import { SelectInfoMessageItem } from "../../../features/doc-items/items/SelectInfoMessageItem.js";
import { TemplatesQuoteTemplateEditSettingsModal } from "../../../features/templates/quote/TemplatesQuoteTemplateSettingsModal.js";
import { TemplatesQuoteTemplateSubHeader } from "../../../features/templates/quote/TemplatesQuoteTemplateSubHeader.js";
import { TemplateQuoteDataGrid } from "./TemplateQuoteDataGrid.js";
import { TemplateQuoteEdit } from "./TemplateQuoteEdit.js";
import { TemplateQuoteEditItem } from "./TemplateQuoteEditItem.js";
import { TemplateQuoteThreeDEdit } from "./TemplateQuoteEditThreeD.js";
import { TemplateQuoteEditTroubleshoot } from "./TemplateQuoteEditTroubleshoot.js";
import { useTemplateQuoteRoutesQuery } from "./TemplateQuoteRoutes.generated.js";
import { UnnestedTemplateQuoteEditItem } from "./UnnestedTemplateQuoteEditItem.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { TemplateQuoteProperties } from "./TemplateQuoteProperties.js";

const REFETCH_QUERIES_LIST = [namedOperations.Query.TemplateQuotes];
const REFETCH_QUERIES_PREVIEW = [
  namedOperations.Query.TemplateQuoteEditPreview,
];
const REFETCH_QUERIES_EDIT = [namedOperations.Query.TemplateQuoteEdit];

type PageSubmenuItem = PageTopbarItem;

interface Props {
  submenuItems: PageSubmenuItem[];
}

export const TemplateQuoteRoutes = ({ submenuItems }: Props) => {
  const { id } = useParams();
  if (!id) throw new Error("Template id is missing");

  const { t } = useTranslate(["SidebarItems", "Global"]);
  const viewer = useUserData().currentUser!;
  const { isMinTablet } = useScreenWidth();
  const { isMinOneColumnWithPreview, isMinTwoColumnsWithPreview } =
    usePageWidth();

  const client = useApolloClient();
  const query = useTemplateQuoteRoutesQuery({
    client,
    variables: { id },
  });

  const doc = getDataOrNull(query.data?.quoteTemplateLatest)?.quoteTemplate;
  const docTitle = doc?.title ?? "";
  const rootItem = doc?.rootItem;
  const isOrganisationOwnTemplate =
    doc?.owningSystemOrganisationId === viewer.organisation.id;
  const canEdit = isOrganisationOwnTemplate;

  const pathToDocList = `/templates/documents`;
  const pathToDoc = `${pathToDocList}/${id}`;
  const pathToDocVersion = `${pathToDoc}/latest`;

  const breadcrumbs = React.useMemo(
    () => [
      {
        title: t("Templates"),
        to: pathToDocList,
      },
      { title: docTitle, to: pathToDocVersion },
    ],
    [docTitle, pathToDocList, pathToDocVersion, t]
  );

  const activeView = useActiveView(pathToDocVersion);

  const pageSubHeader =
    doc && rootItem ? (
      <TemplatesQuoteTemplateSubHeader
        docId={doc.id}
        doc={doc}
        rootItem={rootItem}
        rootItemId={rootItem.id}
        pathToDocList={pathToDocList}
        pathToDoc={pathToDoc}
        pathToDocVersion={pathToDocVersion}
        onDuplicateRefetchQueries={REFETCH_QUERIES_LIST}
        onUpdateDataRefetchQueries={[
          ...REFETCH_QUERIES_PREVIEW,
          ...REFETCH_QUERIES_EDIT,
        ]}
        activeView={activeView}
        isHeaderVisible={undefined as never}
        setHeaderVisible={undefined as never}
        canEdit={canEdit}
        ToggleApplyOverridesButton={null}
        SettingsModalButton={
          <RestrictedByOrganisationPermissionWithDebug
            permission="MANAGE_TEMPLATE_SETTINGS"
            otherwise={
              <ModalOpenButton
                Modal={TemplatesQuoteTemplateEditSettingsModal}
                modalProps={{
                  templateId: doc.id,
                  canEdit: false,
                  pathToDocList,
                }}
              >
                <Tooltip title={t("Info", { ns: "Global" })}>
                  <IconButton size="small" color="primary">
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </ModalOpenButton>
            }
          >
            <ModalOpenButton
              Modal={TemplatesQuoteTemplateEditSettingsModal}
              modalProps={{
                templateId: doc.id,
                refetchQueries: [
                  namedOperations.Query.TemplateQuoteEdit,
                  namedOperations.Query.TemplateQuoteVersion, // TODO: settings should be read-only in version - then this is unnecessary
                  namedOperations.Query.TemplateQuoteEditPreview,
                  namedOperations.Query.TemplateQuoteVersionPreview, // TODO: settings should be read-only in version - then this is unnecessary
                ],
                canEdit,
                pathToDocList,
              }}
            >
              <Tooltip
                title={
                  canEdit
                    ? t("Settings", { ns: "Global" })
                    : t("Info", { ns: "Global" })
                }
              >
                <IconButton size="small" color="primary">
                  {canEdit ? <SettingsIcon /> : <InfoOutlinedIcon />}
                </IconButton>
              </Tooltip>
            </ModalOpenButton>
          </RestrictedByOrganisationPermissionWithDebug>
        }
      />
    ) : null;

  return (
    <Routes>
      <Route index element={<Navigate to={`edit`} replace />} />
      <Route
        path={`edit`}
        element={
          <TemplateQuoteEdit
            docId={id}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            pathToDocPage={`${pathToDocVersion}/edit`}
            submenuItems={submenuItems}
            prefixBreadcrumbs={breadcrumbs}
            pageSubHeader={pageSubHeader}
          />
        }
      >
        <Route
          path={`items/:itemId`}
          element={
            <TemplateQuoteEditItem
              docId={id}
              pathToDocList={pathToDocList}
              pathToDoc={pathToDoc}
              pathToDocPage={`${pathToDocVersion}/edit`}
            />
          }
        />
        {isMinTablet &&
          (isMinTwoColumnsWithPreview || !isMinOneColumnWithPreview) && (
            <Route index element={<SelectInfoMessageItem />} />
          )}
      </Route>
      <Route
        path={`3d`}
        element={
          <TemplateQuoteThreeDEdit
            docId={id}
            pathToDoc={pathToDoc}
            pathToDocVersion={pathToDocVersion}
            pathToDocPage={`${pathToDocVersion}/3d`}
            submenuItems={submenuItems}
            prefixBreadcrumbs={breadcrumbs}
            pathToDocList={pathToDocList}
            pageSubHeader={pageSubHeader}
          />
        }
      >
        <Route
          path={`items/:itemId`}
          element={
            <UnnestedTemplateQuoteEditItem
              docId={id}
              pathToDocList={pathToDocList}
              pathToDocPage={`${pathToDocVersion}/3d`}
              isAbsolute
            />
          }
        />
      </Route>
      <Route
        path={`troubleshoot`}
        element={
          <TemplateQuoteEditTroubleshoot
            docId={id}
            submenuItems={submenuItems}
            prefixBreadcrumbs={breadcrumbs}
            pageSubHeader={pageSubHeader}
            pathToDocList={pathToDocList}
          />
        }
      />
      <Route
        path={`properties`}
        element={
          <TemplateQuoteProperties
            docId={id}
            submenuItems={submenuItems}
            prefixBreadcrumbs={breadcrumbs}
            pageSubHeader={pageSubHeader}
            pathToDocList={pathToDocList}
          />
        }
      />
      <Route
        path={`grid`}
        element={
          query.loading ? (
            <LoadingSpinner />
          ) : !isMinTablet || !canEdit ? (
            <Navigate to={`../edit`} replace />
          ) : (
            <TemplateQuoteDataGrid
              docId={id}
              pathToDocPage={`${pathToDocVersion}/grid`}
              submenuItems={submenuItems}
              prefixBreadcrumbs={breadcrumbs}
              pageSubHeader={pageSubHeader}
            />
          )
        }
      >
        <Route
          path={`items/:itemId`}
          element={
            <TemplateQuoteEditItem
              docId={id}
              pathToDocList={pathToDocList}
              pathToDoc={pathToDoc}
              pathToDocPage={`${pathToDocVersion}/grid`}
              position="right"
              isAbsolute={true}
            />
          }
        />
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

function useActiveView(pathToDoc: string) {
  const matchTree = useMatch(`${pathToDoc}/edit/*`);
  const match3d = useMatch(`${pathToDoc}/3d/*`);
  const matchProperties = useMatch(`${pathToDoc}/properties/*`);
  const matchGrid = useMatch(`${pathToDoc}/grid/*`);

  const activeView: LayoutView | null = matchTree
    ? "tree"
    : match3d
      ? "3d"
      : matchProperties
        ? "properties"
        : matchGrid
          ? "grid"
          : null;

  return activeView;
}
